import gql from "graphql-tag"
import { DataSnapshot, ref, get } from "firebase/database"
import {
  DRIVING_MINUTE_PRODUCT_ID,
  OBSERVATION_MINUTE_PRODUCT_ID,
} from "@coast/utils"

import apolloClientNext from "../ApolloClientNext"
import { StudentBalanceQueryDocument } from "./users.graphqlTypes"
import { sdk9db } from "../firebase"
import { PurchaseLog, User, UserBalance } from "../types"
import { HOME_PICKUP_PRODUCT_ID } from "../components/admin/common/constants"

gql`
  query StudentBalanceQuery(
    $studentId: ID!
    $drivingMinuteProductId: ID!
    $observationMinuteProductId: ID!
    $homePickupProductId: ID!
  ) {
    getStudentBalance(studentId: $studentId) {
      userId

      drivingMinutesBalance: balancedQuantityByProductId(
        productId: $drivingMinuteProductId
      ) {
        availableQuantity
        usedQuantity
        purchasedQuantity
        expiredQuantity
      }

      observationMinutesBalance: balancedQuantityByProductId(
        productId: $observationMinuteProductId
      ) {
        availableQuantity
        usedQuantity
        purchasedQuantity
        expiredQuantity
      }

      homePickupBalance: balancedQuantityByProductId(
        productId: $homePickupProductId
      ) {
        availableQuantity
        usedQuantity
        purchasedQuantity
        expiredQuantity
      }
    }
  }
`

// @TODO Putting this in the users file for now since misc.js
// is not a TS file. Will move back to misc.ts once we
// merge with courses.
export const snapshotToArray = <T>(snapshot: DataSnapshot) => {
  const returnArr: T[] = []

  snapshot.forEach((childSnapshot) => {
    const item = childSnapshot.val()
    item.id = childSnapshot.key
    returnArr.push(item)
  })

  return returnArr
}

export const getUserFromId = async (userId: string) =>
  get(ref(sdk9db, `users/${userId}`)).then((snap) => ({
    id: snap.key,
    ...snap.val(),
  }))

export function removeVoidsFromPurchaseLogs(
  purchaseLogs: PurchaseLog[],
): PurchaseLog[] {
  const allVoidLogs = purchaseLogs.filter((log) => log.isVoid)
  const voidLogIds = allVoidLogs.map((log) => log.id)
  const voidLogLinkedIds = allVoidLogs.map((log) => log.linkedLogId)
  return purchaseLogs.filter(
    (log) => !voidLogIds.includes(log.id) && !voidLogLinkedIds.includes(log.id),
  )
}
export interface TraverseControlObj {
  shouldStop: boolean
}

const IMPERSONATION_KEY = "impersonated"

export function setImpersonation(email: string | undefined) {
  if (!email) {
    window.localStorage.removeItem(IMPERSONATION_KEY)
    return
  }

  window.localStorage.setItem(IMPERSONATION_KEY, JSON.stringify({ email }))
}

export function getImpersonation(): { email: string } | undefined {
  const impersonated = window.localStorage.getItem(IMPERSONATION_KEY)

  try {
    return impersonated ? JSON.parse(impersonated) : undefined
  } catch {
    return undefined
  }
}

export async function getStudentBalance(
  userId?: string,
): Promise<UserBalance | null> {
  if (!userId) {
    return null
  }

  const { data } = await apolloClientNext.query({
    query: StudentBalanceQueryDocument,
    variables: {
      studentId: userId,
      drivingMinuteProductId: DRIVING_MINUTE_PRODUCT_ID,
      observationMinuteProductId: OBSERVATION_MINUTE_PRODUCT_ID,
      homePickupProductId: HOME_PICKUP_PRODUCT_ID,
    },
    fetchPolicy: "network-only",
  })

  if (!data) {
    return null
  }

  const b = data.getStudentBalance
  return {
    drivingMinutesUsed: b.drivingMinutesBalance.usedQuantity,
    observationMinutesUsed: b.observationMinutesBalance.usedQuantity,
    drivingMinutesPurchased: b.drivingMinutesBalance.purchasedQuantity,
    observationMinutesPurchased: b.observationMinutesBalance.purchasedQuantity,
    drivingMinutesRemaining: b.drivingMinutesBalance.availableQuantity,
    observationMinutesRemaining: b.observationMinutesBalance.availableQuantity,
    expirationDrivingMinutesUsed: b.drivingMinutesBalance.expiredQuantity,
    expirationObservationMinutesUsed:
      b.observationMinutesBalance.expiredQuantity,
    homePickupsPurchased: b.homePickupBalance.purchasedQuantity,
    homePickupsUsed: b.homePickupBalance.usedQuantity,
    homePickupsRemaining: b.homePickupBalance.availableQuantity,
    homePickupsExpired: b.homePickupBalance.expiredQuantity,
  }
}

export const getPreviousDrivingHours = (user: User) => {
  return user.previousDrivingHours ?? user.previousHours ?? 0
}
